import { Button as AriaButton, Button } from 'react-aria-components';

import type { Custody } from '@infinex/types';
import { VAULTS_LABEL } from '@infinex/types';
import { Tooltip, TooltipTrigger } from '@infinex/ui/components';

import {
  CustodyEmblemFromWalletId,
  ExternalSignerWalletEmblem,
} from '../custody/CustodyEmblem';

const MAX_VISIBLE_ICONS = 3;
const CAPPED_VISIBLE_COUNT = 2;

export type CustodyEmblemGroupMetadata = {
  type: Custody;
  label: string;
  walletId?: string;
};

type CustodyEmblemGroupProps = {
  custodyMetadata: CustodyEmblemGroupMetadata[];
  /** Visible emblems before collapsing into "+N"; `Infinity` shows all. */
  max?: number;
};

/**
 * Displays overlapping custody emblems with a transparent cut-out effect.
 * Uses CSS mask to create clean separation between overlapping icons.
 */
export const CustodyEmblemGroup = ({
  custodyMetadata,
  max = MAX_VISIBLE_ICONS,
}: CustodyEmblemGroupProps) => {
  const totalCount = custodyMetadata.length;
  const shouldCap = totalCount > max;

  const visibleCustodies = shouldCap
    ? custodyMetadata.slice(0, Math.max(max - 1, CAPPED_VISIBLE_COUNT))
    : custodyMetadata;

  const remainingCount = totalCount - visibleCustodies.length;
  const remainingCustodies = shouldCap
    ? custodyMetadata.slice(visibleCustodies.length)
    : [];

  const custodyIcons: Array<{
    type: Custody;
    walletId?: string;
    key: string;
    zIndex: number;
    label: string;
  }> = [];

  visibleCustodies.forEach((metadata, index) => {
    custodyIcons.push({
      type: metadata.type,
      walletId: metadata.walletId,
      key: metadata.walletId
        ? `${metadata.type}-${metadata.walletId}`
        : `${metadata.type}-${index}`,
      zIndex: totalCount - index,
      label:
        metadata.type === 'vault' && !metadata.label
          ? VAULTS_LABEL
          : metadata.label,
    });
  });

  if (custodyIcons.length === 0) {
    return null;
  }

  return (
    <div className="relative flex items-center justify-start">
      {custodyIcons.map((custodyItem, index) => {
        const isOverlapped = index > 0;

        return (
          <TooltipTrigger key={custodyItem.key}>
            <Button
              className="relative outline-hidden focus-visible:ring-2"
              style={{
                marginLeft: isOverlapped ? '-8px' : '0',
                zIndex: custodyItem.zIndex,
              }}
              aria-label={custodyItem.label}
            >
              {/* Container with circular mask cutout for overlapped items */}
              <div
                className="relative"
                style={
                  isOverlapped
                    ? {
                        // Create a circular cutout where the previous icon overlaps
                        // 14px radius circle centered at -2px from the left edge
                        maskImage:
                          'radial-gradient(circle at -4px 50%, transparent 14px, black 14px)',
                        WebkitMaskImage:
                          'radial-gradient(circle at -4px 50%, transparent 14px, black 14px)',
                      }
                    : {}
                }
              >
                {custodyItem.walletId == null &&
                custodyItem.type === 'external-signer' ? (
                  <ExternalSignerWalletEmblem
                    size="small"
                    prominence="medium"
                  />
                ) : (
                  <CustodyEmblemFromWalletId
                    walletId={custodyItem.walletId ?? 'vault'}
                    size="small"
                    prominence="medium"
                  />
                )}
              </div>
            </Button>

            <Tooltip>{custodyItem.label}</Tooltip>
          </TooltipTrigger>
        );
      })}

      {shouldCap && remainingCount > 0 && (
        <TooltipTrigger>
          <AriaButton className="text-default focus-visible:outline-focusRing ml-1 text-xs font-medium">
            +{remainingCount}
          </AriaButton>

          <Tooltip>{remainingCustodies.map((m) => m.label).join(', ')}</Tooltip>
        </TooltipTrigger>
      )}
    </div>
  );
};
